import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Typography,
  Alert,
  Snackbar,
  Avatar,
} from '@mui/material';
import { Add, Edit, Delete, PhotoCamera } from '@mui/icons-material';
import { TableSortLabel } from '@mui/material';
import { getAllRooms, createRoom, updateRoom, deleteRoom, getRoomTypes } from '../../services/roomService';
import { getAllClinics } from '../../services/clinicService';
import api from '../../services/api';

// Rosie clinic id will be fetched on mount
const ROSIE_CLINIC_CODE = 'ROSIE';

function RoomManagement() {
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [rosieClinicId, setRosieClinicId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState({
    room_number: '',
    room_name: '',
    room_type_id: '',
    resource_type: 'room',
    capacity: 1,
    description: '',
    equipment: [],
    hr_number: '',
    is_active: 1,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Room saved successfully!');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const fileInputRef = useRef();
  const [sortField, setSortField] = useState('room_number');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
  };
  const sorted = [...rooms].sort((a, b) => {
    if (!sortField) return 0;
    const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [roomsData, clinicsData, typesData, equipData] = await Promise.all([
        getAllRooms({ is_active: 'all' }),
        getAllClinics(false),
        getRoomTypes(),
        api.get('/equipment').then(r => r.data),
      ]);
      setRooms(roomsData);
      setRoomTypes(typesData);
      setEquipmentOptions(equipData.map(e => e.name));

      // Find Rosie clinic
      const rosie = clinicsData.find(c => c.clinic_code === ROSIE_CLINIC_CODE) || clinicsData[0];
      if (rosie) setRosieClinicId(rosie.id);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data');
    }
  };

  const handleOpenDialog = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        room_number: room.room_number,
        room_name: room.room_name,
        room_type_id: room.room_type_id || '',
        resource_type: room.resource_type || 'room',
        capacity: room.capacity || 1,
        description: room.description || '',
        equipment: room.equipment ? (typeof room.equipment === 'string' ? JSON.parse(room.equipment) : room.equipment) : [],
        hr_number: room.hr_number || '',
        is_active: room.is_active,
      });
      setPhotoPreview(room.photo_url || '');
    } else {
      setEditingRoom(null);
      setFormData({
        room_number: '',
        room_name: '',
        room_type_id: '',
        resource_type: 'room',
        capacity: 1,
        description: '',
        equipment: [],
        hr_number: '',
        is_active: 1,
      });
      setPhotoPreview('');
    }
    setPhotoFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRoom(null);
    setPhotoFile(null);
    setPhotoPreview('');
    setError('');
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    setError('');

    if (!formData.room_number || !formData.room_name) {
      setError('Room number and room name are required');
      return;
    }

    try {
      const payload = { ...formData, clinic_id: rosieClinicId };
      let savedRoom;
      if (editingRoom) {
        savedRoom = await updateRoom(editingRoom.id, payload);
      } else {
        savedRoom = await createRoom(payload);
      }

      const roomId = savedRoom?.id || editingRoom?.id;

      // Upload photo if selected
      if (photoFile && roomId) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        await api.post(`/rooms/${roomId}/photo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setSuccessMessage('Room saved successfully!');
      setSuccess(true);
      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save room');
    }
  };

  const handleDeleteClick = (room) => {
    setRoomToDelete(room);
    setDeleteError('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roomToDelete) return;
    try {
      await deleteRoom(roomToDelete.id);
      setSuccessMessage('Room deleted successfully!');
      setSuccess(true);
      setDeleteDialogOpen(false);
      setRoomToDelete(null);
      loadData();
    } catch (err) {
      if (err.response?.data?.has_bookings) {
        setDeleteError(`Cannot delete room with ${err.response.data.booking_count} existing booking(s). Set room to inactive instead.`);
      } else {
        setDeleteError(err.response?.data?.error || 'Failed to delete room');
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setRoomToDelete(null);
    setDeleteError('');
  };

  const parseEquipment = (eq) => {
    if (!eq) return [];
    if (Array.isArray(eq)) return eq;
    try { return JSON.parse(eq); } catch { return []; }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Rooms</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Room
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Photo</TableCell>
            {[['room_number','Room Number'],['room_name','Room Name'],['room_type_name','Type'],['resource_type','Resource']].map(([field, label]) => (
              <TableCell key={field} sortDirection={sortField === field ? sortDir : false}>
                <TableSortLabel active={sortField === field} direction={sortField === field ? sortDir : 'asc'} onClick={() => handleSort(field)}>{label}</TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Equipment</TableCell>
            <TableCell sortDirection={sortField === 'is_active' ? sortDir : false}>
              <TableSortLabel active={sortField === 'is_active'} direction={sortField === 'is_active' ? sortDir : 'asc'} onClick={() => handleSort('is_active')}>Status</TableSortLabel>
            </TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((room) => (
            <TableRow key={room.id}>
              <TableCell>
                {room.photo_url ? (
                  <Avatar src={room.photo_url} variant="rounded" sx={{ width: 48, height: 48 }} />
                ) : (
                  <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'grey.200', color: 'grey.500', fontSize: 12 }}>
                    No photo
                  </Avatar>
                )}
              </TableCell>
              <TableCell>{room.room_number}</TableCell>
              <TableCell>{room.room_name}</TableCell>
              <TableCell>{room.room_type_name || '-'}</TableCell>
              <TableCell>
                <Chip label={room.resource_type === 'desk' ? 'Desk' : 'Room'} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {parseEquipment(room.equipment).slice(0, 3).map((eq, idx) => (
                    <Chip key={idx} label={eq} size="small" />
                  ))}
                  {parseEquipment(room.equipment).length > 3 && (
                    <Chip label={`+${parseEquipment(room.equipment).length - 3}`} size="small" variant="outlined" />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Chip label={room.is_active ? 'Active' : 'Inactive'} color={room.is_active ? 'success' : 'default'} size="small" />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleOpenDialog(room)} title="Edit"><Edit fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => handleDeleteClick(room)} title="Delete" color="error"><Delete fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Room Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingRoom ? 'Edit Room' : 'Add Room'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Clinic is hard-coded to Rosie — keep field hidden */}
            <Box sx={{ display: 'none' }}>
              <TextField label="Clinic" value={rosieClinicId} readOnly fullWidth />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Resource Type"
                select
                fullWidth
                value={formData.resource_type}
                onChange={(e) => handleChange('resource_type', e.target.value)}
              >
                <MenuItem value="room">Room</MenuItem>
                <MenuItem value="desk">Desk</MenuItem>
              </TextField>

              <TextField
                select
                label="Room Type"
                fullWidth
                value={formData.room_type_id}
                onChange={(e) => handleChange('room_type_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {roomTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>{type.type_name}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Room Number"
                fullWidth
                required
                value={formData.room_number}
                onChange={(e) => handleChange('room_number', e.target.value)}
                placeholder="e.g., R101"
              />
              <TextField
                label="Room Name"
                fullWidth
                required
                value={formData.room_name}
                onChange={(e) => handleChange('room_name', e.target.value)}
                placeholder="e.g., Consultation Room 1"
              />
            </Box>

            <TextField
              label="HR Number"
              fullWidth
              value={formData.hr_number}
              onChange={(e) => handleChange('hr_number', e.target.value)}
              placeholder="e.g., HR-12345"
            />

            <TextField
              label="Capacity"
              type="number"
              fullWidth
              value={formData.capacity}
              onChange={(e) => handleChange('capacity', parseInt(e.target.value))}
              inputProps={{ min: 1 }}
            />

            <FormControl fullWidth>
              <InputLabel>Equipment</InputLabel>
              <Select
                multiple
                value={formData.equipment}
                onChange={(e) => handleChange('equipment', e.target.value)}
                input={<OutlinedInput label="Equipment" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {equipmentOptions.map((eq) => (
                  <MenuItem key={eq} value={eq}>{eq}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />

            <TextField
              select
              label="Status"
              fullWidth
              value={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.value)}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </TextField>

            {/* Photo upload */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Room Photo</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {photoPreview && (
                  <Box
                    component="img"
                    src={photoPreview}
                    alt="Room preview"
                    sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                  />
                )}
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  onClick={() => fileInputRef.current?.click()}
                  size="small"
                >
                  {photoPreview ? 'Change Photo' : 'Upload Photo'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
              </Box>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRoom ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Room</DialogTitle>
        <DialogContent>
          {roomToDelete && (
            <Box>
              <Typography>Are you sure you want to delete this room?</Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 2 }}>
                <Typography variant="body2"><strong>Room:</strong> {roomToDelete.room_name}</Typography>
                <Typography variant="body2"><strong>Room Number:</strong> {roomToDelete.room_number}</Typography>
              </Box>
              {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)} message={successMessage} />
    </Box>
  );
}

export default RoomManagement;
